import React, { useEffect, useState, useCallback } from 'react';
import { AdminLayout } from '../layout/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Loader2, ShieldAlert, CheckCircle2, XCircle, Trash2, Clock, RefreshCcw } from 'lucide-react';
import { toast } from 'sonner';
import { accountDeletionApi } from '../../lib/api';

export default function AdminAccountDeletionsPage() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState('pending');

    const [approveItem, setApproveItem] = useState(null);
    const [approveDropbox, setApproveDropbox] = useState(true);
    const [approveSubmitting, setApproveSubmitting] = useState(false);

    const [rejectItem, setRejectItem] = useState(null);
    const [rejectReason, setRejectReason] = useState('');
    const [rejectSubmitting, setRejectSubmitting] = useState(false);

    const reload = useCallback(async () => {
        setLoading(true);
        try {
            const res = await accountDeletionApi.listRequests(statusFilter);
            setItems(res.data || []);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Anträge konnten nicht geladen werden');
        } finally {
            setLoading(false);
        }
    }, [statusFilter]);

    useEffect(() => { reload(); }, [reload]);

    const handleApprove = async () => {
        if (!approveItem) return;
        setApproveSubmitting(true);
        try {
            const res = await accountDeletionApi.approve(approveItem.id, approveDropbox);
            const cascade = res.data?.cascade || {};
            const total = Object.values(cascade).reduce((a, b) => a + (b || 0), 0);
            const dbx = res.data?.dropbox?.deleted ? ' · Dropbox-Ordner verschoben' : '';
            toast.success(`Konto gelöscht — ${total} abhängige Datensätze entfernt${dbx}`);
            setApproveItem(null);
            reload();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Löschung fehlgeschlagen');
        } finally {
            setApproveSubmitting(false);
        }
    };

    const handleReject = async () => {
        if (!rejectItem) return;
        setRejectSubmitting(true);
        try {
            await accountDeletionApi.reject(rejectItem.id, rejectReason.trim() || null);
            toast.success('Antrag abgelehnt');
            setRejectItem(null);
            setRejectReason('');
            reload();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Ablehnung fehlgeschlagen');
        } finally {
            setRejectSubmitting(false);
        }
    };

    const StatusTab = ({ value, label }) => (
        <button
            onClick={() => setStatusFilter(value)}
            className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${statusFilter === value ? 'bg-rose-600 text-white' : 'bg-white border text-gray-700 hover:bg-gray-50'}`}
            data-testid={`deletion-status-tab-${value}`}
        >
            {label}
        </button>
    );

    return (
        <AdminLayout>
            <div className="space-y-6 max-w-5xl">
                <div className="flex items-start justify-between gap-4">
                    <div>
                        <h1 className="text-2xl font-heading font-bold flex items-center gap-2">
                            <ShieldAlert className="h-6 w-6 text-rose-700" />
                            Konto-Löschungen (DSGVO)
                        </h1>
                        <p className="text-sm text-muted-foreground mt-1">
                            Selbstständig eingereichte Löschanträge der Akademie-Mitglieder
                            gemäss Art. 17 DSGVO. Bitte zeitnah prüfen und entscheiden.
                        </p>
                    </div>
                    <Button variant="outline" size="sm" onClick={reload} disabled={loading} data-testid="reload-deletion-requests">
                        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCcw className="h-4 w-4" />}
                    </Button>
                </div>

                <div className="flex items-center gap-2">
                    <StatusTab value="pending" label="Offen" />
                    <StatusTab value="rejected" label="Abgelehnt" />
                    <StatusTab value="completed" label="Erledigt" />
                </div>

                {loading ? (
                    <div className="flex justify-center py-12">
                        <Loader2 className="h-8 w-8 animate-spin text-primary" />
                    </div>
                ) : items.length === 0 ? (
                    <Card>
                        <CardContent className="py-12 text-center text-sm text-muted-foreground">
                            Keine Anträge in diesem Status.
                        </CardContent>
                    </Card>
                ) : (
                    <div className="space-y-3" data-testid="deletion-requests-list">
                        {items.map((req) => (
                            <Card key={req.id} className="border-rose-100">
                                <CardHeader>
                                    <div className="flex items-start justify-between gap-4">
                                        <div>
                                            <CardTitle className="text-base">
                                                {req.user_name || req.user_email || '–'}
                                            </CardTitle>
                                            <CardDescription>
                                                {req.user_email}
                                                {' · '}
                                                <Clock className="inline h-3 w-3 mr-0.5" />
                                                {req.requested_at ? new Date(req.requested_at).toLocaleString('de-DE') : '–'}
                                            </CardDescription>
                                        </div>
                                        {req.status === 'pending' && (
                                            <div className="flex gap-2">
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    className="border-gray-300"
                                                    onClick={() => { setRejectItem(req); setRejectReason(''); }}
                                                    data-testid={`reject-${req.id}`}
                                                >
                                                    <XCircle className="h-4 w-4 mr-1" /> Ablehnen
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    className="bg-rose-600 hover:bg-rose-700 text-white"
                                                    onClick={() => { setApproveItem(req); setApproveDropbox(true); }}
                                                    data-testid={`approve-${req.id}`}
                                                >
                                                    <CheckCircle2 className="h-4 w-4 mr-1" /> Genehmigen & Löschen
                                                </Button>
                                            </div>
                                        )}
                                    </div>
                                </CardHeader>
                                <CardContent className="text-sm space-y-2">
                                    {req.reason && (
                                        <div>
                                            <span className="text-xs uppercase tracking-wide text-gray-500 font-medium">Begründung des Mitglieds</span>
                                            <p className="mt-1 italic text-gray-800">„{req.reason}"</p>
                                        </div>
                                    )}
                                    {req.review_reason && (
                                        <div>
                                            <span className="text-xs uppercase tracking-wide text-gray-500 font-medium">Admin-Notiz</span>
                                            <p className="mt-1 text-gray-800">{req.review_reason}</p>
                                        </div>
                                    )}
                                    {req.cascade && (
                                        <div className="text-xs text-gray-600">
                                            <span className="font-medium">Gelöscht:</span>{' '}
                                            {Object.entries(req.cascade).filter(([, v]) => v > 0).map(([k, v]) => `${v} ${k}`).join(', ') || '—'}
                                            {req.dropbox?.deleted && <span> · Dropbox-Ordner ↗ Papierkorb</span>}
                                        </div>
                                    )}
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                )}
            </div>

            {/* Approve dialog */}
            <Dialog open={!!approveItem} onOpenChange={(o) => !o && setApproveItem(null)}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-rose-900">
                            <Trash2 className="h-5 w-5" /> Konto endgültig löschen?
                        </DialogTitle>
                        <DialogDescription>
                            Mitglied <strong>{approveItem?.user_name || approveItem?.user_email}</strong> sowie alle Lizenzen,
                            Verlängerungsanträge, Buchungen, Stornierungen, Rechnungen, Training-Records
                            und QR-Scans werden unwiderruflich aus der Datenbank entfernt.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-xs text-rose-900">
                            Diese Aktion ist nicht rückgängig zu machen. Bitte ggf. zuvor ein Backup
                            ziehen (Einstellungen → Pre-Deployment-Checkliste).
                        </div>
                        <div className="flex items-start gap-2">
                            <Checkbox
                                id="approve-dropbox"
                                checked={approveDropbox}
                                onCheckedChange={setApproveDropbox}
                                data-testid="approve-dropbox-checkbox"
                            />
                            <Label htmlFor="approve-dropbox" className="text-sm leading-snug">
                                Dropbox-Ordner des Mitglieds in den Papierkorb verschieben
                                <span className="block text-xs text-gray-500">Empfohlen — alle PDFs (Lizenzen, Bescheinigungen, Quittungen) werden mit gelöscht. 30 Tage durch Dropbox wiederherstellbar.</span>
                            </Label>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setApproveItem(null)} disabled={approveSubmitting}>
                            Abbrechen
                        </Button>
                        <Button
                            className="bg-rose-600 hover:bg-rose-700 text-white"
                            onClick={handleApprove}
                            disabled={approveSubmitting}
                            data-testid="confirm-approve-deletion"
                        >
                            {approveSubmitting ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Trash2 className="h-4 w-4 mr-1.5" />}
                            Genehmigen & Löschen
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Reject dialog */}
            <Dialog open={!!rejectItem} onOpenChange={(o) => !o && setRejectItem(null)}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Antrag ablehnen</DialogTitle>
                        <DialogDescription>
                            Begründung erscheint im Portal des Mitglieds. Lassen Sie das Feld frei,
                            wenn keine Erläuterung notwendig ist.
                        </DialogDescription>
                    </DialogHeader>
                    <Textarea
                        value={rejectReason}
                        onChange={(e) => setRejectReason(e.target.value)}
                        rows={4}
                        placeholder="z. B. offene Lizenzgebühr, laufende Verlängerung, …"
                        data-testid="reject-reason-input"
                    />
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setRejectItem(null)} disabled={rejectSubmitting}>
                            Abbrechen
                        </Button>
                        <Button
                            onClick={handleReject}
                            disabled={rejectSubmitting}
                            data-testid="confirm-reject-deletion"
                        >
                            {rejectSubmitting ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <XCircle className="h-4 w-4 mr-1.5" />}
                            Antrag ablehnen
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </AdminLayout>
    );
}
